import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Grid, Button, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { CalendarMonth, KeyboardArrowDown, Cake, Favorite, Public, Air } from '@mui/icons-material';
import './SpecialDaysEDMs.css';

const templates = [
  { label: 'World Refugee Day', icon: Public, color: '#0b91d6' },
  { label: 'Happy Father\'s Day', icon: Favorite, color: '#16a34a' },
  { label: 'Happy Mother\'s Day', icon: Cake, color: '#ec4899' },
  { label: 'World Wind Day', icon: Air, color: '#8b5cf6' },
];

const SpecialDaysEDMs = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    handleClose();
    navigate('../special-days-photo-edms', { 
      state: { selectedSpecialDay: template.label } 
    });
  };

  return (
    <Box sx={{ p: 4, background: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{
        background: '#0b91d6',
        borderRadius: '16px',
        padding: '32px 40px',
        marginBottom: '32px',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-50%',
          right: '-5%',
          width: '200px',
          height: '200px',
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '50%'
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <CalendarMonth sx={{ fontSize: 36, color: 'white' }} />
          <Box>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, color: 'white', fontFamily: 'Inter, sans-serif' }}>
              Special Days EDMs
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '15px', fontFamily: 'Inter, sans-serif' }}>
              Create beautiful designs for special occasions
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Selection Section */}
      <Paper sx={{ 
        p: 5, 
        borderRadius: '16px', 
        border: '1px solid #e2e8f0',
        boxShadow: 'none',
        background: 'white',
        textAlign: 'center'
      }}>
        <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#1e293b', mb: 1, fontFamily: 'Inter, sans-serif' }}>
          Select Your Special Day
        </Typography>
        <Typography sx={{ fontSize: '15px', color: '#64748b', mb: 4, maxWidth: 500, mx: 'auto' }}>
          Choose a special occasion to create custom marketing materials
        </Typography>

        {/* Dropdown Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Button
            onClick={handleClick}
            variant="outlined"
            endIcon={<KeyboardArrowDown />}
            sx={{
              minWidth: 280,
              py: 1.5,
              px: 3,
              borderRadius: '12px',
              borderColor: '#e2e8f0',
              color: selectedTemplate ? '#1e293b' : '#64748b',
              fontWeight: 500,
              fontSize: '15px',
              textTransform: 'none',
              justifyContent: 'space-between',
              '&:hover': {
                borderColor: '#0b91d6',
                background: '#f8fafc'
              }
            }}
          >
            {selectedTemplate?.label || 'Select a special day'}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            PaperProps={{
              sx: {
                minWidth: 280,
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                mt: 1
              }
            }}
          >
            {templates.map((template) => {
              const IconComponent = template.icon;
              return (
                <MenuItem 
                  key={template.label} 
                  onClick={() => handleTemplateSelect(template)}
                  sx={{
                    py: 1.5,
                    '&:hover': {
                      background: `${template.color}10`
                    }
                  }}
                >
                  <ListItemIcon>
                    <IconComponent sx={{ color: template.color }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={template.label}
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: '14px'
                    }}
                  />
                </MenuItem>
              );
            })}
          </Menu>
        </Box>

        {/* Quick Selection Cards */}
        <Typography sx={{ fontSize: '14px', color: '#94a3b8', mb: 3 }}>
          Or quickly select from popular options:
        </Typography>
        <Grid container spacing={2} justifyContent="center">
          {templates.map((template) => {
            const IconComponent = template.icon;
            return (
              <Grid item xs={6} sm={3} key={template.label}>
                <Paper
                  onClick={() => handleTemplateSelect(template)}
                  sx={{
                    p: 3,
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: template.color,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${template.color}20`
                    }
                  }}
                >
                  <Box sx={{
                    width: 48, height: 48, borderRadius: '12px',
                    bgcolor: `${template.color}15`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    mx: 'auto', mb: 2
                  }}>
                    <IconComponent sx={{ fontSize: 24, color: template.color }} />
                  </Box>
                  <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#1e293b' }}>
                    {template.label}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    </Box>
  );
};

export default SpecialDaysEDMs; 